import React, { use, useCallback } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { AuthManagerContext } from "../../contexts/AuthManager";
import TextField from "../../components/TextField";
import ScreenCard from "../ScreenCard";
import VStack from "../../ui_atoms/VStack";
import { isValidAVSNumber } from "../../avs";
import { avsMask } from "../../utils";
import { useMutation } from "@apollo/client/react";
import { UPDATE_WORKER_PROFILE } from "../../graphql/core";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";

type ScreenParams = StackScreenProps<RootStackParamList>;

const AvsNumberEditorScreen = ({ navigation }: ScreenParams) => {
    const { user, refreshUser } = use(AuthManagerContext);
    const i18n = useI18n();

    const initialAvsNumber = user?.workerProfile?.avsNumber || undefined;
    const [avsNumber, setAvsNumber] = React.useState<string | undefined>(initialAvsNumber);

    const [
        updateWorkerProfile,
        { loading: updateLoading, error: updateError, reset: updateReset },
    ] = useMutation(UPDATE_WORKER_PROFILE);

    useApolloErrorSnackbar(updateError, updateReset, i18n.t("errors.saving"));

    const onSubmit = useCallback(async () => {
        await updateWorkerProfile({
            variables: {
                changes: {
                    avsNumber: avsNumber || null,
                },
            },
        });
        void refreshUser?.();
        navigation.goBack();
    }, [avsNumber, navigation, refreshUser, updateWorkerProfile]);

    return (
        <ScreenCard
            buttons={
                !initialAvsNumber
                    ? [
                          {
                              title: i18n.t("save"),
                              onPress: onSubmit,
                              mode: "secondary",
                              validating: updateLoading,
                              isValid: isValidAVSNumber(avsNumber || ""),
                          },
                      ]
                    : []
            }
        >
            <VStack h="2/3" v="2/3">
                <TextField
                    value={avsNumber}
                    onChangeText={setAvsNumber}
                    keyboardType="numeric"
                    mask={avsMask}
                    maskAutoComplete
                    label={i18n.t("profile.avs_number")}
                    disabled={initialAvsNumber !== undefined}
                />
            </VStack>
        </ScreenCard>
    );
};

export default AvsNumberEditorScreen;
